import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { showToast } from "../../utils/toastUtils";
import "./VerifyCode.css";
import loginBgImage from "../../assets/images/Login_Page_bg.png";
import logoImage from "../../assets/images/AstrocureLogo.svg";
import { callApi } from "../../utils/apiService";
import { videoCreatorEndPoint } from "../../Constants/constants";
import { useUser } from "../../Context/UserContext";

const VerifyCode = () => {
  const [formData, setFormData] = useState({
    otp_code: "",
  });

  const [codeError, setCodeError] = useState("");
  const [timer, setTimer] = useState(60);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { passwordReset, setPasswordResetData } = useUser();

  // Timer effect
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  // Check if email exists in context
  useEffect(() => {
    if (!passwordReset.email) {
      showToast("error", "Please go through forgot password first");
      navigate("/forgot-password");
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Allow only numbers and max 6 digits
    const numericValue = value.replace(/\D/g, "").slice(0, 6);

    setFormData((prev) => ({
      ...prev,
      [name]: numericValue,
    }));

    // Clear error on input change
    setCodeError("");
  };

  const validateCode = () => {
    const code = formData.otp_code.trim();

    if (!code) {
      setCodeError("Verification code is required");
      return false;
    }

    if (code.length !== 6) {
      setCodeError("Please enter a 6-digit code");
      return false;
    }

    setCodeError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateCode()) {
      return;
    }

    setLoading(true);
    try {
      const response = await callApi(
        `${videoCreatorEndPoint}auth/verify-forgot-password-otp`,
        "POST",
        {
          email: passwordReset.email,
          otp_code: formData.otp_code,
        }
      );

      if (response.status) {
        // Store resetToken in context
        const { resetToken } = response.data;
        setPasswordResetData({ resetToken });

        // Show success toast
        showToast("success", "OTP verified successfully", 100);

        // Navigate to create new password page
        setTimeout(() => {
          navigate("/create-new-password");
        }, 1000);
      } else {
        showToast("error", response.alert || response.message || "OTP verification failed");
      }
    } catch (error) {
      showToast("error", error.message || "OTP verification failed. Please try again.");
      console.error("Verify OTP error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div
        className="login-left"
        style={{ backgroundImage: `url(${loginBgImage})` }}
      >
        <div className="mobile-wave">
          <svg
            className="mobile-wave-svg"
            viewBox="0 0 1440 320"
            preserveAspectRatio="none"
          >
            <path
              fill="#f5f5f5"
              d="M0,224L80,202.7C160,181,320,139,480,154.7C640,171,800,245,960,250.7C1120,256,1280,192,1360,160L1440,128L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z"
            />
          </svg>
        </div>
      </div>
      {/* MID CURVE (SEPARATE ELEMENT) */}
      <div className="mid-curve">
        <svg
          className="mid-curve-svg"
          viewBox="0 0 520 1000"
          preserveAspectRatio="none"
        >
          <path
            d="
              M 520 0
              L 270 0
              C 220 70, 205 140, 245 210
              C 310 330, 175 430, 140 520
              C 100 640, 260 730, 275 840
              C 290 930, 225 1000, 170 1000
              L 520 1000
              Z
            "
            fill="#f5f5f5"
          />
        </svg>
      </div>

      {/* Right side */}
      <div className="login-right">
        <div className="login-card">
          <div className="login-logo">
            <img src={logoImage} alt="AstroCure" className="login-logo-image" />
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <div className="one-step-text">Just one step left. Verify your email</div>
              <div className="one-step-desc">Please check your inbox for the verification code sent to <span style={{color: '#65269C'}}>{passwordReset.email}</span>.</div>
              <div className="floating-group">
                <label htmlFor="otp_code" className="floating-label">
                  Code <span className="required">*</span>
                </label>

                <input
                  type="text"
                  id="otp_code"
                  name="otp_code"
                  className={`form-input floating-input ${codeError ? "form-input-error" : ""}`}
                  placeholder="Enter the 6-digit code here"
                  value={formData.otp_code}
                  onChange={handleInputChange}
                  maxLength="6"
                  required
                  disabled={loading}
                />
                {codeError && <p className="verify-error">{codeError}</p>}
              </div>
            </div>

            {timer > 0 && <p className="verify-timer">{timer} sec</p>}

            <button type="submit" className="verify-code-button-cls" disabled={loading}>
              {loading ? "Verifying..." : "Verify Code"}
            </button>

            <p className="cant-find-label-cls">Can't find it ? Please check your spam folder</p> 
            <button 
              type="button" 
              onClick={() => { navigate("/forgot-password");}}
              className="frgt-back-button"
              disabled={timer > 0 || loading}
            >
              Resend Code
            </button>
          </form>
        </div>

        <footer className="login-footer">
          Copyright © 2026 Astrovidhi Private Limited All rights reserved
        </footer>
      </div>
    </div>
  );
};

export default VerifyCode;
